/**
 * Self-contained helpers for the AT Protocol OAuth browser client.
 *
 * These reimplement the few primitives that the upstream
 * `@atcute/oauth-browser-client` package pulls in from `@atcute/multibase`,
 * `@atcute/uint8array` and friends, so that this module has no runtime
 * dependency at all — only the platform (WebCrypto, fetch). Keeping it
 * dependency-free is what lets the resolver be unit-tested in isolation.
 */

const encoder = /*#__PURE__*/ new TextEncoder();

/** Encode a UTF-8 string into bytes. */
export const encodeUtf8 = (input: string): Uint8Array => {
	return encoder.encode(input);
};

/** Encode bytes as a base64url string (no padding). */
export const toBase64Url = (bytes: Uint8Array): string => {
	let str = '';
	for (let i = 0, len = bytes.length; i < len; i++) {
		str += String.fromCharCode(bytes[i]);
	}

	return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** Decode a base64url string back into bytes. */
export const fromBase64Url = (input: string): Uint8Array => {
	const padded = input.replace(/-/g, '+').replace(/_/g, '/');
	const binary = atob(padded);

	const bytes = new Uint8Array(binary.length);
	for (let i = 0, len = binary.length; i < len; i++) {
		bytes[i] = binary.charCodeAt(i);
	}

	return bytes;
};

/** SHA-256 digest of arbitrary bytes. */
export const sha256 = async (bytes: Uint8Array): Promise<Uint8Array> => {
	const digest = await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>);
	return new Uint8Array(digest);
};

/** base64url(SHA-256(utf8(input))), used for PKCE challenges and DPoP `ath`. */
export const stringToSha256 = async (input: string): Promise<string> => {
	return toBase64Url(await sha256(encodeUtf8(input)));
};

/** Web Locks API, when available, used to serialise token refreshes across tabs. */
export const locks: LockManager | undefined =
	typeof navigator !== 'undefined' ? navigator.locks : undefined;

export const extractContentType = (headers: Headers): string | undefined => {
	return headers.get('content-type')?.split(';')[0];
};

export const isValidUrl = (urlString: string): boolean => {
	let url: URL | null = null;

	try {
		url = new URL(urlString);
	} catch {}

	if (url !== null) {
		return url.protocol === 'https:' || url.protocol === 'http:';
	}

	return false;
};

type UnwrapArray<T> = T extends (infer V)[] ? V : never;

export const pick = <T, K extends (keyof T)[]>(obj: T, keys: K): Pick<T, UnwrapArray<K>> => {
	const cloned: any = {};

	for (let idx = 0, len = keys.length; idx < len; idx++) {
		const key = keys[idx];
		cloned[key] = obj[key];
	}

	return cloned;
};
